import Card from 'components/card';
import Pagination from 'components/pagination/Pagination';
import { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { getBiPareto } from 'interfaces/bi';
import { formatCurrency, formatNumber, formatPercent } from './helpers';

function ParetoCard() {
    const [data, setData] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const [meta, setMeta] = useState<any>(null);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [loading, setLoading] = useState(true);

    const fetchData = async (p = page, l = limit) => {
        setLoading(true);
        const res = await getBiPareto({ page: p, limit: l });
        setData(res?.data ?? []);
        setSummary(res?.summary ?? null);
        setMeta(res?.meta ?? null);
        setPage(p);
        setLimit(l);
        setLoading(false);
    };

    useEffect(() => {
        fetchData(1, limit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const stockoutRate = Number(summary?.stockoutRate) || 0;

    const gaugeOptions: any = {
        chart: { type: 'radialBar' },
        colors: [stockoutRate < 10 ? '#22c55e' : stockoutRate < 25 ? '#f59e0b' : '#ef4444'],
        plotOptions: {
            radialBar: {
                hollow: { size: '60%' },
                dataLabels: {
                    name: { fontSize: '12px', color: '#A3AED0', offsetY: 20 },
                    value: {
                        fontSize: '26px',
                        fontWeight: 700,
                        color: '#1B2559',
                        offsetY: -10,
                        formatter: (v: number) => `${Math.round(v)}%`,
                    },
                },
            },
        },
        labels: ['Taux de rupture'],
        stroke: { lineCap: 'round' },
    };

    return (
        <Card extra="p-5 border h-full">
            <div className="mb-3">
                <h4 className="text-lg font-bold text-navy-700">Analyse 20/80 (Pareto)</h4>
                <p className="text-sm text-gray-500">
                    Taux de rupture sur les produits générant ~80% du CA
                </p>
            </div>

            {loading ? (
                <div className="flex h-[300px] items-center justify-center">
                    <p className="text-gray-400">Chargement...</p>
                </div>
            ) : !summary?.available ? (
                <div className="flex h-[300px] flex-col items-center justify-center gap-2">
                    <p className="text-gray-400">Analyse non encore calculée.</p>
                    <p className="text-xs text-gray-400">
                        Le job BI quotidien la construit automatiquement.
                    </p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {/* Gauge */}
                        <div className="flex flex-col items-center justify-center">
                            <ReactApexChart
                                options={gaugeOptions}
                                series={[stockoutRate]}
                                type="radialBar"
                                height={240}
                            />
                            <p className="text-center text-xs text-gray-500">
                                {formatNumber(summary?.outOfStockCount)} produit(s) en rupture
                                sur {formatNumber(summary?.paretoProductCount)} du top 80%
                            </p>
                        </div>

                        {/* Pareto stats */}
                        <div className="flex flex-col justify-center gap-3">
                            <div className="rounded-2xl border bg-white px-4 py-3">
                                <p className="text-xs font-medium text-gray-600">
                                    Produits "vitaux" (20%)
                                </p>
                                <h3 className="text-lg font-bold text-navy-700">
                                    {formatNumber(summary?.paretoProductCount)}{' '}
                                    <span className="text-sm font-normal text-gray-400">
                                        / {formatNumber(summary?.totalProductCount)} produits
                                    </span>
                                </h3>
                                <p className="text-[11px] text-gray-400">
                                    soit {formatPercent(summary?.paretoSharePct)} du catalogue
                                </p>
                            </div>
                            <div className="rounded-2xl border bg-white px-4 py-3">
                                <p className="text-xs font-medium text-gray-600">
                                    CA couvert (période d'analyse)
                                </p>
                                <h3 className="text-lg font-bold text-navy-700">
                                    {formatCurrency(summary?.totalRevenueHT)}
                                </h3>
                                {summary?.windowFrom && summary?.windowTo && (
                                    <p className="text-[11px] text-gray-400">
                                        du {summary.windowFrom} au {summary.windowTo}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Mobile cards ── */}
                    <div className="mt-4 space-y-2 sm:hidden">
                        {data.map((p) => (
                            <div
                                key={p.productKey}
                                className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 px-3 py-2"
                            >
                                <div className="min-w-0 flex-1 pr-3">
                                    <p
                                        className="truncate text-sm font-medium text-navy-700"
                                        title={p.name}
                                    >
                                        {p.name || '—'}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                        {formatCurrency(p.revenueHT)} · {formatPercent(p.cumulativePct)} cumulé
                                    </p>
                                </div>
                                <span
                                    className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
                                        p.isOutOfStock
                                            ? 'bg-red-100 text-red-700'
                                            : 'bg-green-100 text-green-700'
                                    }`}
                                >
                                    {p.isOutOfStock ? 'Rupture' : 'En stock'}
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* ── Desktop table ── */}
                    <div className="mt-4 hidden sm:block">
                        <table className="w-full table-fixed">
                            <colgroup>
                                <col className="w-[45%]" />
                                <col className="w-[22%]" />
                                <col className="w-[18%]" />
                                <col className="w-[15%]" />
                            </colgroup>
                            <thead>
                                <tr className="border-b border-gray-200 text-left">
                                    <th className="py-2 text-xs font-bold text-gray-600">Produit</th>
                                    <th className="py-2 text-right text-xs font-bold text-gray-600">CA HT</th>
                                    <th className="py-2 text-right text-xs font-bold text-gray-600">% cumulé</th>
                                    <th className="py-2 text-center text-xs font-bold text-gray-600">Stock</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((p) => (
                                    <tr
                                        key={p.productKey}
                                        className="border-b border-gray-100 hover:bg-gray-50"
                                    >
                                        <td className="overflow-hidden py-2 pr-3">
                                            <p
                                                className="truncate text-sm font-medium text-navy-700"
                                                title={p.name}
                                            >
                                                {p.name || '—'}
                                            </p>
                                        </td>
                                        <td className="py-2 text-right text-sm text-navy-700">
                                            {formatCurrency(p.revenueHT)}
                                        </td>
                                        <td className="py-2 text-right text-sm text-navy-700">
                                            {formatPercent(p.cumulativePct)}
                                        </td>
                                        <td className="py-2 text-center">
                                            <span
                                                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                                                    p.isOutOfStock
                                                        ? 'bg-red-100 text-red-700'
                                                        : 'bg-green-100 text-green-700'
                                                }`}
                                            >
                                                {p.isOutOfStock ? 'Rupture' : 'En stock'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        extra="mt-3"
                        text={false}
                        type="produits"
                        maxItems={meta ? meta.total : 0}
                        itemsPerPage={[10, 25, 50]}
                        page={page}
                        defaultItemsPerPage={limit}
                        onChange={(p: number, l: number) => fetchData(p, l)}
                    />
                </>
            )}
        </Card>
    );
}

export default ParetoCard;
